import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Head from 'next/head';
import { defineMessages, useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { BackHeader, Button, Center, Icon, LinkButton, Modal, Page } from 'src/components';
import { DailyNewWordsResponse, PointsResponse, ProgressResponse } from 'src/api/me/types';
import { UserPathBlob } from 'src/api/userPath/types';
import { useStreakData } from 'src/hooks/useStreakData';
import { LearnablesList } from 'src/apps/ScenarioGeneric/LearnablesList';
import { ScenarioHeader } from 'src/apps/ScenarioGeneric/ScenarioHeader';
import {
  getLocalStorageEntry,
  removeLocalStorageEntry,
  setLocalStorageEntry,
} from 'src/utils/webStorageUtils';
import { useUserContext } from 'src/context/user';
import { StreakApi } from 'src/api';
import { StreakCelebration } from 'src/apps/ScenarioGeneric/StreakCelebration';
import { ROUTES } from 'src/constants/routes.const';
import { buildCommitmentUpsellUrl } from 'src/apps/Payment/Plans/utils/commitmentPageUrl';
import useSafePush from 'src/hooks/useSafePush';
import { ActivitySource } from 'src/utils/analytics/analytics.const';
import {
  DailyNewWords,
  getCanonicalSessionType,
  ProgressPreferences,
  SessionType,
  SummaryItem,
} from 'src/api/learningSessions/types';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { useEligibility } from 'src/hooks/useEligibility';
import { convertUrlToTriggerRefresh } from 'src/utils/convertUrlToTriggerRefresh';
import * as S from 'src/apps/LearningSession/EndOfSession/styles';
import { EndOfSessionLearn2024 } from 'src/apps/LearningSession/EndOfSession/EndOfSessionLearn2024';
import { EndOfSessionLearn } from 'src/apps/LearningSession/EndOfSession/EndOfSessionLearn';
import { useExperienceContext, ExperienceTypes } from 'src/context/experience';
import { useBackCta } from 'src/hooks';
import { doesNotHaveInSoftSellStorageEntry } from 'src/apps/Monetisation/SoftSellScreen/softSellStorageEntry';
import { SoftSellScreen } from 'src/apps/Monetisation/SoftSellScreen';
import { twentyFourHoursAgo } from 'src/apps/DashboardFrame/utils/twentyFourHoursAgo';
import { PathContinueButtons, PathExit } from 'src/apps/Path/PathContinueButtons';
import { PathAutoContinue } from 'src/apps/Path/PathAutoContinue';
import { buildContinueOnPathQuery } from 'src/apps/Path/continueOnPathQuery';
import { MilestoneUpsell } from 'src/apps/Monetisation/MilestoneUpsell';
import { canShowMilestoneUpsell } from 'src/apps/Monetisation/MilestoneUpsell/milestoneUpsellStorage';
import { featureFlags } from 'src/apps/LearningSession/EndOfSession/softSellFeatureFlags';
import { MistakeUpsell } from 'src/apps/Monetisation/MistakeUpsell';
import { canShowMistakeUpsell } from 'src/apps/Monetisation/MistakeUpsell/mistakeUpsellStorage';
import { PronunciationUpsell } from 'src/apps/Monetisation/PronunciationUpsell';
import { canShowPronunciationUpsell } from 'src/apps/Monetisation/PronunciationUpsell/pronunciationUpsellStorage';
import { isNotProduction } from 'src/utils/env/isNotProduction';
import { DEBUG_FORCE_MISTAKE_UPSELL_STORAGE_KEY } from 'src/constants/storageKeys.const';
import { useAdvanceTrackWordlistOnComplete } from 'src/apps/LearningSession/EndOfSession/useAdvanceTrackWordlistOnComplete';
import { BlobEndOfSession } from './BlobEndOfSession';
import { LoadingSkeleton as BlobEndOfSessionSkeleton } from './BlobEndOfSession/LoadingSkeleton';
import { RemindersScreen } from './RemindersScreen';
import { doesNotHaveSeenRemindersScreen } from './RemindersScreen/remindersStorageEntry';
import { CalendarReminder } from './CalendarReminder';
import { useCalendarReminderEligibility } from './CalendarReminder/useCalendarReminder';

const msgs = defineMessages({
  title: { id: 'scenario_end_of_session_title' },
  reviewQueueIncompleteHeader: { id: 'scenario_end_of_session_completed_review_session' },
  reviewQueueCompleteHeader: { id: 'scenario_end_of_session_review_queue_completed' },
  practiceQueueCompleteHeader: { id: 'eos_practice_session_completed' },
  difficultWordsQueueIncompleteHeader: { id: 'web_ea_eos_difficult_words_queue_tbc' },
  difficultWordsQueueCompleteHeader: { id: 'web_ea_eos_difficult_words_completed' },
  wordsReviewed: { id: 'scenario_end_of_session_words_reviewed' },
  wordsJustPracticed: { id: 'eos_dw_wordsJustPracticed_title' },
  wordsStarted: { id: 'web_ea_eos_words_started' },
  continue: { id: 'Continue' },
  learningSettings: { id: 'web_ls_menu' },
  continueReview: { id: 'scenario_end_of_session_continue_reviewing' },
  continuePractice: { id: 'beta_eos_activity_difficultWords_CTA' },
  done: { id: 'eos_reviewQueueCleared_CTA' },
  watchVideo: { id: 'recommended_activity_card_listening_video_up_next_main_CTA' },
  dailyGoalReached: { id: 'daily_activities_interstitial_completed_learn' },
  dailyGoalProgress: { id: 'dashboard_next_lesson_words_learnt' },
});

export const EOS_PLANS_PAGE = 'EOS-PLANS-PAGE';

type EndOfSessionProps = {
  pathwayNodeId?: number | null;
  summaryItems: SummaryItem[];
  levelName: string;
  iconSrc: string;
  sessionType: SessionType;
  hasUserScenario: boolean;
  isPremiumScenario: boolean;
  progressPreferences: { [key: number]: ProgressPreferences };
  hasReviewQueue?: boolean;
  userScenarioCompleted?: boolean;
  progressBefore?: ProgressResponse | null;
  pointsBefore?: PointsResponse | null;
  languageName: string;
  sourceLanguageName?: string;
  dailyNewWords?: DailyNewWords;
  isBlobExperience?: boolean;
  dailyNewWordsBefore?: DailyNewWordsResponse;
  analyticsSessionId?: string;
  completedWordlists?: UserPathBlob[];
};

export const EndOfSession: FC<EndOfSessionProps> = ({
  pathwayNodeId = null,
  summaryItems,
  levelName,
  iconSrc,
  sessionType,
  hasUserScenario,
  isPremiumScenario,
  progressPreferences,
  hasReviewQueue,
  userScenarioCompleted,
  progressBefore,
  pointsBefore,
  languageName,
  sourceLanguageName,
  dailyNewWords,
  isBlobExperience = false,
  dailyNewWordsBefore,
  analyticsSessionId = '',
  completedWordlists,
}) => {
  const intl = useIntl();
  const router = useRouter();
  const backCta = useBackCta();
  const { safePush, safeReplace } = useSafePush();
  const languagePairId = getLanguagePairId();
  const { user } = useUserContext();
  const { isEligibleForPathways } = useEligibility(languagePairId);
  const { _2024, _2025 } = ExperienceTypes;
  const { experienceType } = useExperienceContext();
  const [showStreak, setShowStreak] = useState(false);
  const [showMilestoneUpsell, setShowMilestoneUpsell] = useState(false);
  const [showMistakeUpsell, setShowMistakeUpsell] = useState(false);
  const [showPlansPage, setShowPlansPage] = useState(getLocalStorageEntry(EOS_PLANS_PAGE));
  const [softSellDismissed, setSoftSellDismissed] = useState(false);
  const [hasLatchedLearnSoftSell, setHasLatchedLearnSoftSell] = useState(false);
  const [hasLatchedPronunciationUpsell, setHasLatchedPronunciationUpsell] = useState(false);
  /*
   * Prevent showing Upsell then Softsell back-to-back in Learn 2025 flow.
   * If a wordlists soft-sell would be eligible, skip the plans page.
   */
  const isLearnSoftSellEligible = useMemo(
    () =>
      featureFlags.isLearnWordlistSoftSellEnabled &&
      sessionType === SessionType.LEARN &&
      experienceType === _2025 &&
      doesNotHaveInSoftSellStorageEntry(user, 'wordlists'),
    [user, sessionType, experienceType, _2025],
  );

  /*
   * SoftSellScreen writes to the soft-sell storage entry as it mounts, so eligibility turns
   * false while the screen is still on show. `user` is a fresh object on every context
   * refetch (hourly, and after a purchase), so eligibility would recompute mid-session and
   * pull the soft-sell out from under the learner, replacing it with the Upsell. Latching
   * keeps the session on the screen it started, and keeps the Upsell suppressed once the
   * soft-sell has been dismissed.
   */
  useEffect(() => {
    if (isLearnSoftSellEligible) setHasLatchedLearnSoftSell(true);
  }, [isLearnSoftSellEligible]);

  const learnSoftSellOwnsSession = isLearnSoftSellEligible || hasLatchedLearnSoftSell;
  const showLearnSoftSell = learnSoftSellOwnsSession && !softSellDismissed;

  const isLearn = sessionType === SessionType.LEARN;

  /*
   * Pronunciation upsell, shown once after the learner's very first Learn session. "First
   * session" uses the same account-age proxy as the reminders screen below - see the comment
   * there for why a points-based check doesn't work. Path learners only: the screen sells
   * pronunciation sessions, which only exist on the path.
   */
  const isPronunciationUpsellEligible = useMemo(
    () =>
      isLearn &&
      experienceType === _2025 &&
      !!user?.dateJoined &&
      twentyFourHoursAgo(Date.parse(user.dateJoined)) &&
      canShowPronunciationUpsell(user) &&
      isEligibleForPathways,
    [isLearn, experienceType, _2025, user, isEligibleForPathways],
  );

  /* Latched for the same reason as the soft-sell above: the screen marks itself as seen on
   * mount, so eligibility flips to false while the learner is still looking at it. */
  useEffect(() => {
    if (isPronunciationUpsellEligible) setHasLatchedPronunciationUpsell(true);
  }, [isPronunciationUpsellEligible]);

  const pronunciationUpsellOwnsSession =
    isPronunciationUpsellEligible || hasLatchedPronunciationUpsell;

  /*
   * A points-based "first session" check doesn't hold up: self-reporting a skill level
   * above complete-beginner during onboarding pre-credits points/known-words on the
   * backend before the learner ever finishes a real session, so totalPoints is often
   * already non-zero on their genuine first Learn session. Account age is a more direct
   * signal for "just registered".
   *
   * Users who signed up via Facebook before it requested the "email" scope (see #9741)
   * have no email on file, so the reminder this screen sells can never actually reach
   * them - skip it rather than promise a nudge that won't arrive.
   *
   * Path learners only: the screen sits on the Path learn EOS, and the reminder copy
   * assumes that experience. Classic / non-Path language pairs never see it.
   *
   * Eligibility is only read at the moment the learner tries to leave the lesson-completed
   * screen (see `handleBeforeLearnExit`), not derived reactively into the render - once
   * `pendingRemindersExit` is set, the screen's visibility no longer depends on `user`, so a
   * mid-session refetch can't pull it out from under the learner.
   */
  const isRemindersEligible = useMemo(
    () =>
      isLearn &&
      !!user?.email &&
      !!user?.dateJoined &&
      twentyFourHoursAgo(Date.parse(user.dateJoined)) &&
      doesNotHaveSeenRemindersScreen(user) &&
      isEligibleForPathways,
    [isLearn, user, isEligibleForPathways],
  );
  const {
    isEligible: isCalendarReminderEligible,
    isLoading: calendarDataLoading,
    sessionsToday,
    wordsLearntToday,
    finishedAt,
  } = useCalendarReminderEligibility({
    languagePairId,
    pathwayNodeId,
    enabled: isLearn || pathwayNodeId !== null,
  });
  const [pendingExit, setPendingExit] = useState<(() => void) | null>(null);
  const [exitStage, setExitStage] = useState<
    'pronunciation_upsell' | 'reminders' | 'calendar_reminder' | 'calendar_pending' | null
  >(null);
  /**
   * Passed to the lesson-completed screen as `onBeforeExit`: called with whichever exit
   * action the learner picks, letting us hold interstitials back until they have actually
   * seen their results. Interstitials are chained rather than shown together.
   */
  const handleBeforeLearnExit = useCallback(
    (proceed: () => void, exit: PathExit) => {
      /*
       * Continue only. "Go back to my path" is the learner choosing to stop, so selling
       * into it would be interrupting an exit rather than extending a session.
       */
      if (exit === 'continue' && pronunciationUpsellOwnsSession) {
        setPendingExit(() => proceed);
        setExitStage('pronunciation_upsell');
        return;
      }

      if (exit === 'continue' && calendarDataLoading) {
        setPendingExit(() => proceed);
        setExitStage('calendar_pending');
        return;
      }

      if (exit === 'continue' && isCalendarReminderEligible) {
        setPendingExit(() => proceed);
        setExitStage('calendar_reminder');
        return;
      }

      if (isRemindersEligible) {
        setPendingExit(() => proceed);
        setExitStage('reminders');
        return;
      }

      proceed();
    },
    [
      calendarDataLoading,
      isCalendarReminderEligible,
      isRemindersEligible,
      pronunciationUpsellOwnsSession,
    ],
  );
  const finishExit = () => {
    const proceed = pendingExit;
    setPendingExit(null);
    setExitStage(null);
    proceed?.();
  };
  const dismissPronunciationUpsell = () => {
    if (calendarDataLoading) {
      setExitStage('calendar_pending');
      return;
    }
    if (isCalendarReminderEligible) {
      setExitStage('calendar_reminder');
      return;
    }
    if (isRemindersEligible) {
      setExitStage('reminders');
      return;
    }
    finishExit();
  };
  useEffect(() => {
    if (exitStage !== 'calendar_pending' || calendarDataLoading) return;

    if (isCalendarReminderEligible) {
      setExitStage('calendar_reminder');
      return;
    }

    if (isRemindersEligible) {
      setExitStage('reminders');
      return;
    }

    const proceed = pendingExit;
    setPendingExit(null);
    setExitStage(null);
    proceed?.();
  }, [
    calendarDataLoading,
    exitStage,
    isCalendarReminderEligible,
    isRemindersEligible,
    pendingExit,
  ]);
  /*
   * On the Path, a learner who turns down an upsell or the plans page is taken straight
   * into their next session instead of back to the lesson-completed screen - including when
   * they follow an upsell's CTA to the full plans page and leave it without upgrading. The
   * pronunciation upsell is chained off that screen's Continue button, so it keeps its own
   * exit logic.
   */
  const isPathLearnSession = isLearn && experienceType === _2025 && isEligibleForPathways;
  const plansQuery = isPathLearnSession ? buildContinueOnPathQuery(pathwayNodeId) : undefined;
  const [continueOnPath, setContinueOnPath] = useState(false);
  const exitUpsellWithoutUpgrading = (hideUpsell: () => void) => {
    hideUpsell();
    if (isPathLearnSession) setContinueOnPath(true);
  };
  const isDifficultWords = sessionType === SessionType.DIFFICULT_WORDS;
  const isReview =
    sessionType === SessionType.REVIEW ||
    sessionType === SessionType.REVIEW_ALEX ||
    sessionType === SessionType.MIXED;
  // Review modes launch without a pathway node, so only a path Review stone carries one.
  const isPathReviewSession = isReview && pathwayNodeId !== null;
  // In Learn EOS, only the learn soft-sell should suppress Upsell; in non-Learn EOS, only the review soft-sell should.
  const shouldSuppressUpsellDueToSoftSell = isLearn
    ? learnSoftSellOwnsSession || pronunciationUpsellOwnsSession
    : false;
  let activitySource;

  if (isReview) {
    activitySource = ActivitySource.EOR;
  } else if (isDifficultWords) {
    activitySource = ActivitySource.EODW;
  } else {
    activitySource = ActivitySource.EOL;
  }

  useAdvanceTrackWordlistOnComplete(languagePairId, isLearn && experienceType === _2025);

  const { contentMediaId, back } = router.query;
  const backUrl = back ? String(back) : undefined;

  const { streakData, isLoading: isStreakDataLoading } = useStreakData({
    languagePairId,
    waitForAccomplished: true,
  });

  const sessionCouldHavePlansPage = user && !user.isPro && (experienceType === _2024 || isLearn);
  useEffect(() => {
    if (sessionCouldHavePlansPage) {
      setLocalStorageEntry(
        EOS_PLANS_PAGE,
        getLocalStorageEntry(EOS_PLANS_PAGE) === 'yes' ? 'no' : 'yes',
      );
    }
  }, [sessionCouldHavePlansPage]);

  const learnables = summaryItems?.map(
    ({ item, definition, growthLevel, learnableId, dueForReview }) => ({
      targetValue: item.value,
      sourceValue: definition.value,
      growthLevel,
      alreadyKnown: progressPreferences[learnableId].ignored,
      difficult: sessionType !== 'difficult_words' && progressPreferences[learnableId].difficult,
      learnableId,
      dueForReview,
    }),
  );

  /*
   * SummaryItem carries no per-session correct/incorrect flag or errors-this-session count
   * from the learning-session API. growthLevel is the item's SRS level after the session,
   * so an item still at growthLevel 0 (and not already known) is the closest available proxy
   * for "the learner got this wrong this session". Replace this with a real per-session
   * accuracy/error count if the learning-session summary payload ever exposes one.
   */
  const missedWords = summaryItems
    .filter(
      ({ learnableId, growthLevel }) =>
        growthLevel === 0 && !progressPreferences[learnableId].ignored,
    )
    .map(({ item, definition }) => ({ targetValue: item.value, sourceValue: definition.value }));
  const sessionAccuracy =
    summaryItems.length > 0 ? (summaryItems.length - missedWords.length) / summaryItems.length : 1;
  const meetsMistakeUpsellThreshold =
    missedWords.length > 0 && (sessionAccuracy < 0.6 || missedWords.length >= 4);
  const wouldShowStreakOrMilestoneUpsell = Boolean(
    (streakData?.currentStreak?.accomplishedToday &&
      !streakData?.currentStreak?.hasBeenAcknowledged) ||
    (streakData?.currentStreak?.count === 7 && canShowMilestoneUpsell(user, 'streak_7')),
  );

  const continueUrl = hasReviewQueue
    ? router.asPath.includes('&key=')
      ? router.asPath.substring(0, router.asPath.lastIndexOf('&'))
      : router.asPath
    : null;

  const practiceCompletedHeader = msgs.practiceQueueCompleteHeader;
  const queueCompleteHeader = isDifficultWords
    ? msgs.difficultWordsQueueCompleteHeader
    : msgs.reviewQueueCompleteHeader;
  const queueIncompleteHeader = isDifficultWords
    ? msgs.difficultWordsQueueIncompleteHeader
    : msgs.reviewQueueIncompleteHeader;
  const queueReview = !continueUrl ? queueCompleteHeader : queueIncompleteHeader;
  const headerReview =
    router.query.source_element === 'scenario_details' ? practiceCompletedHeader : queueReview;

  useEffect(() => {
    if (
      !streakData?.currentStreak?.accomplishedToday ||
      streakData?.currentStreak?.hasBeenAcknowledged
    )
      return;

    /*
     * A current streak of 1 with no longer streak ever recorded means the user has never
     * had a streak before this - i.e. this is their first-ever accomplished session. Skip
     * the celebration and silently acknowledge it so it doesn't reappear on Home either.
     */
    const isFirstEverStreakDay =
      streakData.currentStreak.count === 1 && (streakData.longestStreak?.count ?? 0) <= 1;

    if (isFirstEverStreakDay) {
      if (languagePairId) void StreakApi.postStreak(languagePairId, { acknowledged: true });
      return;
    }

    setShowStreak(true);
  }, [streakData, languagePairId]);

  useEffect(() => {
    const debugForceMistakeUpsell =
      isNotProduction() && getLocalStorageEntry(DEBUG_FORCE_MISTAKE_UPSELL_STORAGE_KEY) === 'true';

    if (debugForceMistakeUpsell) {
      removeLocalStorageEntry(DEBUG_FORCE_MISTAKE_UPSELL_STORAGE_KEY);
      setShowMistakeUpsell(true);
      return;
    }

    if (
      !wouldShowStreakOrMilestoneUpsell &&
      !pronunciationUpsellOwnsSession &&
      meetsMistakeUpsellThreshold &&
      canShowMistakeUpsell(user)
    ) {
      setShowMistakeUpsell(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streakData]);

  /*
   * Streak and mistake screens are chosen in effects, so the first render after the streak
   * loads does not know about them yet. Wait one commit before opening the upsell page,
   * otherwise a learner who earned a celebration would be sent away before it appears.
   */
  const [eosDecisionReady, setEosDecisionReady] = useState(false);
  useEffect(() => {
    if (isStreakDataLoading || !experienceType) return;
    setEosDecisionReady(true);
  }, [experienceType, isStreakDataLoading, streakData]);

  const shouldOpenCommitmentUpsell =
    eosDecisionReady &&
    showPlansPage === 'yes' &&
    !!sessionCouldHavePlansPage &&
    !shouldSuppressUpsellDueToSoftSell &&
    !showStreak &&
    !showMilestoneUpsell &&
    !showMistakeUpsell &&
    !continueOnPath;
  const hasOpenedCommitmentUpsell = useRef(false);
  useEffect(() => {
    if (!shouldOpenCommitmentUpsell || hasOpenedCommitmentUpsell.current) return;
    hasOpenedCommitmentUpsell.current = true;

    const open = async () => {
      try {
        await safeReplace(
          buildCommitmentUpsellUrl(
            'eos_vocab',
            isPathLearnSession
              ? {
                  continueOnPath: true,
                  pathwayNodeId,
                  sessionType: 'vocab' as const,
                  showReminders: isRemindersEligible,
                }
              : { back: backUrl },
          ),
        );
      } catch {
        setShowPlansPage('no');
      }
    };

    void open();
  }, [
    backUrl,
    isPathLearnSession,
    isRemindersEligible,
    pathwayNodeId,
    safeReplace,
    shouldOpenCommitmentUpsell,
  ]);

  const plansDecisionPending =
    showPlansPage === 'yes' &&
    !!sessionCouldHavePlansPage &&
    !shouldSuppressUpsellDueToSoftSell &&
    !eosDecisionReady;

  if (isStreakDataLoading || !experienceType || plansDecisionPending) {
    if (isBlobExperience) return <BlobEndOfSessionSkeleton />;

    return (
      <S.LoaderWrapper>
        <S.Loader />
      </S.LoaderWrapper>
    );
  }

  if (showStreak)
    return (
      <div data-testid="streak-celebration">
        <StreakCelebration
          languagePairId={languagePairId}
          count={streakData?.currentStreak?.count}
          isOpen={showStreak}
          handleClose={() => {
            setShowStreak(false);
            if (
              !pronunciationUpsellOwnsSession &&
              streakData?.currentStreak?.count === 7 &&
              canShowMilestoneUpsell(user, 'streak_7')
            ) {
              setShowMilestoneUpsell(true);
            }
          }}
          isFullSize
        />
      </div>
    );

  const exitInterstitials = (
    <>
      {exitStage === 'pronunciation_upsell' && (
        <PronunciationUpsell onContinue={dismissPronunciationUpsell} />
      )}
      {exitStage === 'calendar_pending' && (
        <Modal isOpen fullSize hideCloseButton handleClose={() => {}}>
          <S.LoaderWrapper data-testid="calendar-reminder-pending">
            <S.Loader />
          </S.LoaderWrapper>
        </Modal>
      )}
      {exitStage === 'calendar_reminder' && (
        <CalendarReminder
          languageName={languageName}
          wordsLearntToday={wordsLearntToday}
          sessionsToday={sessionsToday}
          streakCount={streakData?.currentStreak?.count ?? 0}
          streakAccomplishedToday={!!streakData?.currentStreak?.accomplishedToday}
          finishedAt={finishedAt}
          onContinue={finishExit}
          onDecline={finishExit}
        />
      )}
      {exitStage === 'reminders' && <RemindersScreen onSelect={finishExit} onSkip={finishExit} />}
    </>
  );

  const isFirstSession =
    !!progressBefore &&
    progressBefore.learnablesFullyGrownCount +
      progressBefore.learnablesTouchedButNotFullyGrownCount ===
      0;

  const blobEndOfSession = isBlobExperience && (
    <>
      <BlobEndOfSession
        summaryItems={summaryItems}
        completedWordlists={completedWordlists}
        progressPreferences={progressPreferences}
        isFirstSession={isFirstSession}
        pathwayNodeId={pathwayNodeId}
        pathSessionType={isLearn || sessionType === SessionType.MIXED ? 'vocab' : null}
        continueReviewUrl={isReview ? continueUrl : null}
        dailyNewWordsBefore={dailyNewWordsBefore}
        sessionId={analyticsSessionId}
        sessionType={getCanonicalSessionType(sessionType)}
        onBeforeExit={handleBeforeLearnExit}
      />
      {exitInterstitials}
    </>
  );

  if (continueOnPath) {
    return (
      <>
        <PathAutoContinue
          languagePairId={languagePairId}
          pathwayNodeId={pathwayNodeId}
          sessionType="vocab"
          onBeforeNavigate={handleBeforeLearnExit}
        />
        {exitInterstitials}
      </>
    );
  }

  if (showMilestoneUpsell) {
    return (
      <MilestoneUpsell
        milestone="streak_7"
        plansQuery={plansQuery}
        onContinue={() => exitUpsellWithoutUpgrading(() => setShowMilestoneUpsell(false))}
      />
    );
  }

  if (showMistakeUpsell) {
    return (
      <MistakeUpsell
        missedWords={missedWords}
        plansQuery={plansQuery}
        onContinue={() => exitUpsellWithoutUpgrading(() => setShowMistakeUpsell(false))}
      />
    );
  }

  if (shouldOpenCommitmentUpsell) {
    return (
      <S.LoaderWrapper data-testid="plans-page">
        <S.Loader />
      </S.LoaderWrapper>
    );
  }

  if (isLearn) {
    /* istanbul ignore next: please test in WEBSITE-7219 */
    if (experienceType === _2024 && !isBlobExperience) {
      return (
        <div data-testid="end-of-session-learn">
          <EndOfSessionLearn2024
            hasUserScenario={hasUserScenario}
            iconSrc={iconSrc}
            isPremiumScenario={isPremiumScenario}
            userScenarioCompleted={userScenarioCompleted}
            levelName={levelName}
            progressBefore={progressBefore}
            pointsBefore={pointsBefore}
            languageName={languageName}
          />
        </div>
      );
    } else if (experienceType === _2025 || isBlobExperience) {
      if (showLearnSoftSell) {
        return (
          <SoftSellScreen
            screenType="wordlists"
            trackingType="learn"
            onContinue={() => setSoftSellDismissed(true)}
          />
        );
      }

      if (blobEndOfSession) return blobEndOfSession;

      return (
        <>
          <div data-testid="end-of-session-learn">
            <EndOfSessionLearn
              summaryItems={summaryItems}
              pathwayNodeId={pathwayNodeId}
              onBeforeExit={handleBeforeLearnExit}
            />
          </div>
          {exitInterstitials}
        </>
      );
    } else {
      return null;
    }
  }

  if (blobEndOfSession) return blobEndOfSession;

  return (
    <Page footerStyle="none">
      <Head>
        <title>{intl.formatMessage(msgs.title)} - Memrise</title>
      </Head>
      <BackHeader href={backUrl} />
      <S.FullWidthFloatingPortal>
        <div id="floatingBottomPortalRoot" />
      </S.FullWidthFloatingPortal>
      <S.Root data-testid="scenarios-EndOfSession">
        <S.Content>
          <Center $centerAlignItems>
            <S.ContentWrapper $align="center">
              <ScenarioHeader
                title={levelName}
                iconSrc={iconSrc}
                isPremiumScenario={isPremiumScenario}
                reviewSessionCompleted={!hasReviewQueue}
                showAnimation={!hasReviewQueue}
                sessionType={sessionType}
              />

              <S.Header>{intl.formatMessage(headerReview)}</S.Header>
              {dailyNewWords && (
                <S.DailyGoalText>
                  {intl.formatMessage(
                    dailyNewWords.startedToday >= dailyNewWords.goal
                      ? msgs.dailyGoalReached
                      : msgs.dailyGoalProgress,
                    { learned: dailyNewWords.startedToday, total: dailyNewWords.goal },
                  )}
                </S.DailyGoalText>
              )}
              <S.ButtonContainer>
                {isPathReviewSession ? (
                  <PathContinueButtons
                    languagePairId={languagePairId}
                    pathwayNodeId={pathwayNodeId}
                    onBeforeNavigate={handleBeforeLearnExit}
                  />
                ) : (
                  <>
                    {continueUrl && (
                      <LinkButton
                        buttonType="primary"
                        buttonSize="large"
                        href={convertUrlToTriggerRefresh(continueUrl)}
                      >
                        {intl.formatMessage(
                          isDifficultWords ? msgs.continuePractice : msgs.continueReview,
                        )}
                      </LinkButton>
                    )}
                    <LinkButton
                      buttonType={continueUrl ? 'secondary' : 'primary'}
                      buttonSize="large"
                      href={backCta.href}
                    >
                      {
                        /* istanbul ignore next: please test in WEBSITE-7219 */
                        experienceType === _2025 ? backCta.text : intl.formatMessage(msgs.done)
                      }
                    </LinkButton>
                  </>
                )}
                {contentMediaId && (
                  <Button
                    buttonType="secondary"
                    onClick={() =>
                      safePush({
                        pathname: ROUTES.video.media,
                        query: {
                          contentMediaId,
                          languagePairId,
                          source: activitySource,
                        },
                      })
                    }
                  >
                    {intl.formatMessage(msgs.watchVideo)}
                  </Button>
                )}
              </S.ButtonContainer>
              <S.WordsWrapper>
                <S.WordsSection>
                  <S.WordsCard>
                    <S.WordsCardText>
                      {intl.formatMessage(isReview ? msgs.wordsReviewed : msgs.wordsJustPracticed)}
                    </S.WordsCardText>
                    <S.WordsCardText>{summaryItems.length}</S.WordsCardText>
                  </S.WordsCard>
                </S.WordsSection>
              </S.WordsWrapper>
              <S.ListWrapper>
                <LearnablesList
                  isLoading={false}
                  list={learnables}
                  targetLanguageName={languageName}
                  sourceLanguageName={sourceLanguageName}
                />
              </S.ListWrapper>
            </S.ContentWrapper>
          </Center>
        </S.Content>
        <S.SettingsIconWrapper
          onClick={async () => {
            await safePush(ROUTES.settings.learning);
          }}
          aria-label={intl.formatMessage(msgs.learningSettings)}
        >
          <Icon name="settingsFilled" />
        </S.SettingsIconWrapper>
      </S.Root>
      {exitInterstitials}
    </Page>
  );
};
